import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UnorderedListOutlined } from "@ant-design/icons";
import { Col, Pagination, Row, Modal } from "antd";

import { selectIsPerfumeDeleted } from "../../../redux-toolkit/admin/admin-selector";
import {
  selectIsPerfumesLoading,
  selectPerfumes,
} from "../../../redux-toolkit/perfumes/perfumes-selector";
import {
  fetchPerfumes,
  fetchPerfumesByInputText,
} from "../../../redux-toolkit/perfumes/perfumes-thunks";
import { resetPerfumesState } from "../../../redux-toolkit/perfumes/perfumes-slice";
import { resetAdminState } from "../../../redux-toolkit/admin/admin-slice";
import ContentTitle from "../../../components/ContentTitle/ContentTitle";
import SelectSearchData from "../../../components/SelectSearchData/SelectSearchData";
import InputSearch from "../../../components/InputSearch/InputSearch";
import PerfumeCard from "../../../components/PerfumeCard/PerfumeCard";
import { deletePerfume } from "../../../redux-toolkit/admin/admin-thunks";
import { LoadingStatus, PerfumeResponse } from "../../../types/types";
import DeleteModal from "./DeleteModal/DeleteModal";
import Spinner from "../../../components/Spinner/Spinner";
import { MAX_PAGE_VALUE, usePagination } from "../../../hooks/usePagination";
import { useSearch } from "../../../hooks/useSearch";
import "./PerfumeList.css";

const PerfumeList: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const perfumes = useSelector(selectPerfumes);
  const isPerfumesLoading = useSelector(selectIsPerfumesLoading);
  const isPerfumeDeleted = useSelector(selectIsPerfumeDeleted);
  const [perfumeInfo, setPerfumeInfo] = useState<PerfumeResponse>();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const { currentPage, totalElements, handleChangePagination } = usePagination();
  const { searchValue, searchTypeValue, onSearch, handleChangeSelect } = useSearch();

  useEffect(() => {
    dispatch(fetchPerfumes(0));
    return () => {
      dispatch(resetPerfumesState());
      dispatch(resetAdminState(LoadingStatus.LOADING));
    };
  }, []);

  useEffect(() => {
    if (isPerfumeDeleted) {
      setIsModalVisible(false);
      Modal.success({
        title: "Deleted Successfully",
        content: `${perfumeInfo?.perfumeTitle || "Product"} has been deleted successfully.`,
        okText: "OK",
        onOk: () => {
          dispatch(fetchPerfumes(currentPage - 1 >= 0 ? currentPage - 1 : 0));
        },
      });
      dispatch(resetAdminState(LoadingStatus.LOADED));
    }
  }, [isPerfumeDeleted]);

  const changePagination = (page: number, pageSize: number): void => {
    if (searchValue) {
      dispatch(
        fetchPerfumesByInputText({
          searchType: searchTypeValue,
          text: searchValue,
          currentPage: page - 1,
        })
      );
    } else {
      dispatch(fetchPerfumes(page - 1));
    }
    handleChangePagination(page, pageSize);
  };

  const showDeleteModalWindow = (perfume: PerfumeResponse): void => {
    setIsModalVisible(true);
    setPerfumeInfo(perfume);
  };

  const deletePerfumeHandler = (): void => {
    if (perfumeInfo?.id) {
      dispatch(deletePerfume(perfumeInfo.id));
    }
  };

  const handleCancel = (): void => {
    setIsModalVisible(false);
  };

  return (
    <div className="pl-page">

      {/* Top bar */}
      <div className="pl-topbar">
        <div className="pl-topbar-left">
          <UnorderedListOutlined className="pl-topbar-icon" />
          <span className="pl-topbar-title">List of Products</span>
          {totalElements > 0 && (
            <span className="pl-topbar-badge">{totalElements} items</span>
          )}
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="pl-breadcrumb">
        Dashboard &rsaquo; Products
      </div>

      <Row>
        <Col span={24}>

          {/* Search strip */}
          <div className="pl-search-bar">
            <Row gutter={[12, 12]} align="middle">
              <Col span={9}>
                <SelectSearchData handleChangeSelect={handleChangeSelect} />
              </Col>
              <Col span={10}>
                <InputSearch onSearch={onSearch} />
              </Col>
            </Row>
          </div>

          {isPerfumesLoading ? (
            <Spinner />
          ) : (
            <>
              {/* Top pagination strip */}
              <div className="pl-pagination-strip">
                <span className="pl-count-text">
                  Showing <strong>{perfumes.length}</strong> of{" "}
                  <strong>{totalElements}</strong> products
                </span>
                <Pagination
                  current={currentPage}
                  pageSize={MAX_PAGE_VALUE}
                  total={totalElements}
                  showSizeChanger={false}
                  onChange={changePagination}
                  size="small"
                />
              </div>

              {/* Product grid */}
              <div className="pl-grid-wrapper">
                <Row gutter={[16, 16]}>
                  {perfumes.map((perfume) => (
                    <Col key={perfume.id} xs={24} sm={12} md={8} lg={6}>
                      <div className="pl-card-wrap">
                        <PerfumeCard
                          perfume={perfume}
                          isAdminView={true}
                          onDelete={showDeleteModalWindow}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>

                {perfumes.length === 0 && (
                  <div className="pl-empty">
                    <div className="pl-empty-icon">🔍</div>
                    <p className="pl-empty-text">No products found.</p>
                  </div>
                )}
              </div>

              {/* Bottom pagination */}
              <div className="pl-pagination-bottom">
                <Pagination
                  current={currentPage}
                  pageSize={MAX_PAGE_VALUE}
                  total={totalElements}
                  showSizeChanger={false}
                  onChange={changePagination}
                />
              </div>
            </>
          )}
        </Col>
      </Row>

      <DeleteModal
        visible={isModalVisible}
        deletePerfumeHandler={deletePerfumeHandler}
        handleCancel={handleCancel}
        perfumeInfo={perfumeInfo}
      />
    </div>
  );
};

export default PerfumeList;